import { useState } from "react"
import { Tag, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { formatMoney } from "./money"
import { quotePickupCoupon, type PickupCouponResult } from "@/services/user-pickup-checkouts"
import { t } from "@/lib/i18n"

/**
 * Where a diner types a promotion code.
 *
 * ## It quotes; it does not redeem
 *
 * Applying a code here spends nothing. `POST /r/:slug/coupon` answers what the
 * code is worth against this cart and writes no redemption; the checkout POST
 * re-asks every question under the coupon's row lock and is the only write path.
 * So the figure on screen is for reading, and the code — not the amount — is
 * what travels to checkout.
 *
 * That asymmetry is deliberate rather than defensive. A code that can only be
 * tested by attempting a payment is a code nobody tests, and the diner is owed
 * "ya venció" or "ya se usó" *before* being sent to Stripe, not after.
 *
 * ## The refusal sentence is the server's
 *
 * Expired and already-used are different facts living in different columns, and
 * only the server can tell them apart. This component renders whatever sentence
 * came back and never maps a reason code to copy of its own — a client-side map
 * is how the two collapse into one generic "código inválido", which is precisely
 * the failure this feature is judged on.
 *
 * ## No code primitive exists
 *
 * None of the components under `components/ui` provides an OTP/pin field, so
 * this is a plain `Input` at the kit's two heights with upcasing on the way in.
 * Codes are stored `strip.upcase`, and a diner typing lowercase must not be told
 * their code does not exist.
 */

type CouponFieldProps = {
  slug: string
  /** The cart as the checkout endpoint reads it, so the quote prices the real lines. */
  cart: unknown[]
  currency: string
  /**
   * The typed string. Owned by the cart screen, not by this component, because
   * a quote goes stale the moment the cart changes: the screen drops the quote
   * and keeps the string, so the diner re-applies with a tap instead of
   * retyping a code they already got right.
   */
  code: string
  onCodeChange: (value: string) => void
  /** The applied code, owned by the cart screen because checkout has to send it. */
  applied: AppliedCoupon | null
  onApply: (coupon: AppliedCoupon) => void
  onClear: () => void
}

export type AppliedCoupon = {
  code: string
  promotionName: string
  /** Exact decimal string, the server's. Display only — the charge re-derives it. */
  discountAmount: string
}

export function CouponField({
  slug,
  cart,
  currency,
  code,
  onCodeChange,
  applied,
  onApply,
  onClear,
}: CouponFieldProps) {
  const [checking, setChecking] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)

  if (applied) {
    return (
      <div
        className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/50 px-3 py-2"
        data-testid="pickup-coupon-applied"
      >
        <span className="flex min-w-0 items-center gap-2 text-sm">
          <Tag aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 truncate font-medium">{applied.promotionName || applied.code}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <span
            className="text-sm font-semibold tabular-nums text-foreground"
            data-testid="pickup-coupon-amount"
          >
            −{formatMoney(applied.discountAmount, { currency })}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              onClear()
              // Removed on purpose, so the string goes too. A cart change also
              // clears the quote, and there the string is deliberately kept.
              onCodeChange("")
              setRefusal(null)
            }}
            data-testid="pickup-coupon-remove"
            aria-label={t("ui.react.features.user.coupon_field.remove")}
          >
            <X className="size-4" />
          </Button>
        </span>
      </div>
    )
  }

  const apply = async () => {
    const normalized = code.trim().toUpperCase()
    if (checking) return
    if (normalized.length === 0) {
      setRefusal(t("ui.react.features.user.coupon_field.empty"))
      return
    }

    setChecking(true)
    setRefusal(null)

    try {
      const result = await quotePickupCoupon({ slug, cart, code: normalized })

      if (result.ok) {
        onApply({
          code: result.code,
          promotionName: result.promotionName,
          discountAmount: result.discountAmount,
        })
        // The server's normalisation, not the browser's guess at it.
        onCodeChange(result.code)
        return
      }

      setRefusal(refusalMessage(result))
    } catch {
      setRefusal(t("ui.react.features.user.coupon_field.network_error"))
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="space-y-1">
      <Label htmlFor="pickup-coupon-code" className="text-xs text-muted-foreground">
        {t("ui.react.features.user.coupon_field.label")}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id="pickup-coupon-code"
          name="coupon_code"
          value={code}
          // Upcased on the way in rather than on submit, so the field shows the
          // diner the same string the restaurant printed on the flyer.
          onChange={(event) => onCodeChange(event.target.value.toUpperCase())}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return
            // The cart screen's pay button is the form's natural submit; Enter
            // here must apply the code, not open a Stripe session for a cart the
            // diner still thinks is about to get cheaper.
            event.preventDefault()
            void apply()
          }}
          placeholder={t("ui.react.features.user.coupon_field.placeholder")}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={refusal ? true : undefined}
          aria-describedby={refusal ? "pickup-coupon-refusal" : undefined}
          className="font-mono tracking-wider"
          data-testid="pickup-coupon-input"
        />
        <Button
          type="button"
          variant="secondary"
          className="h-11 shrink-0 md:h-9"
          disabled={checking}
          onClick={apply}
          data-testid="pickup-coupon-apply"
        >
          {checking
            ? t("ui.react.features.user.coupon_field.applying")
            : t("ui.react.features.user.coupon_field.apply")}
        </Button>
      </div>
      {refusal && (
        <p
          id="pickup-coupon-refusal"
          role="status"
          className="text-xs text-destructive"
          data-testid="pickup-coupon-refusal"
        >
          {refusal}
        </p>
      )}
    </div>
  )
}

/**
 * Every refusal `Location::CouponRedemptionService.refusal_reason` can produce,
 * to the sentence the diner reads.
 *
 * Written out one by one rather than interpolated into `ui.errors.${reason}`,
 * for two reasons that both bite. A missing key makes `t` return the key itself,
 * so a reason this map has not heard of would render `ui.errors.coupon_whatever`
 * at a diner. And a lookup that cannot be read by eye is a lookup nobody
 * notices collapsing: **`coupon_expired` and `coupon_already_redeemed` are two
 * different sentences and this feature is judged on their staying that way.**
 *
 * The checkout POST needs this map. The quote endpoint does not — it sends the
 * sentence itself, which is why `refusalMessage` prefers the server's.
 */
export const COUPON_REFUSAL_KEYS: Record<string, string> = {
  coupon_not_found: "ui.errors.coupon_not_found",
  coupon_expired: "ui.errors.coupon_expired",
  coupon_already_redeemed: "ui.errors.coupon_already_redeemed",
  coupon_revoked: "ui.errors.coupon_revoked",
  coupon_promotion_inactive: "ui.errors.coupon_promotion_inactive",
  coupon_outside_window: "ui.errors.coupon_outside_window",
  coupon_nothing_to_discount: "ui.errors.coupon_nothing_to_discount",
  coupon_line_already_discounted: "ui.errors.coupon_line_already_discounted",
}

/** True when a checkout refusal was about the code rather than about the cart. */
export function couponRefusal(reason: string): boolean {
  return reason.startsWith("coupon_")
}

/**
 * The sentence for a refusal that arrived as a bare reason code — which is the
 * shape the checkout POST refuses in, since it shares one `reason` field with
 * `location_closed` and `changed`.
 */
export function couponRefusalMessage(reason: string): string {
  const key = COUPON_REFUSAL_KEYS[reason]

  return key ? t(key) : t("ui.react.features.user.coupon_field.generic")
}

/**
 * The server's sentence, or the one fallback the client is allowed.
 *
 * `rate_limited` and `network_error` are the two refusals the server may answer
 * without a message — the limiter renders a bare reason, and a dropped
 * connection renders nothing at all. Every other reason, including the two this
 * feature is judged on, arrives already spelled out and is passed through
 * untouched.
 */
export function refusalMessage(result: Extract<PickupCouponResult, { ok: false }>): string {
  if (result.message) return result.message

  if (result.reason === "rate_limited") {
    return t("ui.react.features.user.coupon_field.rate_limited")
  }

  if (couponRefusal(result.reason)) return couponRefusalMessage(result.reason)

  return t("ui.react.features.user.coupon_field.network_error")
}
